import { useRef, useState } from "react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Loader2, Save } from "lucide-react";
import SubmitCourtDialog from "@/components/SubmitCourtDialog";

const PLAY_STYLES = ["Aggressive Baseliner", "Net Rusher", "All-Court", "Dinker / Soft Game", "Counter-Puncher"];

export default function Profile() {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    location: user?.location || "",
    dupr_rating: user?.dupr_rating ?? "",
    play_style: user?.play_style || "",
    bio: user?.bio || "",
  });

  if (!user) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin" /></div>;

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name?.trim() || undefined,
        location: form.location?.trim() || undefined,
        dupr_rating: form.dupr_rating === "" ? undefined : parseFloat(form.dupr_rating),
        play_style: form.play_style || undefined,
        bio: form.bio ?? undefined,
      };
      const { data } = await http.put("/users/me", payload);
      setUser(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/users/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser(data);
      toast.success("Profile picture updated");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="profile-page">
      <h1 className="text-3xl sm:text-4xl font-black pv-text-dark mb-1">Your Profile</h1>
      <p className="text-sm text-black/50 mb-8">Manage how other players see you on the court.</p>

      <div className="rounded-3xl bg-white border border-black/5 shadow-sm p-6 sm:p-8">
        {/* Avatar */}
        <div className="flex items-center gap-5 mb-8">
          <div className="relative">
            <Avatar className="h-24 w-24 border-4 pv-border-primary">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback className="text-2xl">{user.name?.[0] || "P"}</AvatarFallback>
            </Avatar>
            <button
              type="button"
              data-testid="avatar-upload-trigger"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute -bottom-1 -right-1 h-9 w-9 grid place-items-center rounded-full pv-bg-primary text-black shadow-md hover:scale-105 transition-transform disabled:opacity-60"
              title="Change photo"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              className="hidden"
              data-testid="avatar-file-input"
              onChange={handleFile}
            />
          </div>
          <div>
            <div className="font-bold text-lg pv-text-dark">{user.name}</div>
            <div className="text-sm text-black/50">{user.email}</div>
            <div className="text-xs text-black/40 mt-1">JPG, PNG, GIF or WEBP · max 5MB</div>
          </div>
        </div>

        {/* Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label htmlFor="name">Display name</Label>
            <Input id="name" data-testid="profile-name-input" value={form.name} onChange={(e) => set("name")(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input id="location" data-testid="profile-location-input" value={form.location} onChange={(e) => set("location")(e.target.value)} placeholder="Charlotte, NC" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dupr">DUPR rating</Label>
            <Input id="dupr" data-testid="profile-dupr-input" type="number" step="0.1" min="1" max="8" value={form.dupr_rating} onChange={(e) => set("dupr_rating")(e.target.value)} placeholder="3.5" />
          </div>
          <div className="space-y-2">
            <Label>Play style</Label>
            <Select value={form.play_style} onValueChange={set("play_style")}>
              <SelectTrigger data-testid="profile-style-trigger"><SelectValue placeholder="Pick a style" /></SelectTrigger>
              <SelectContent>
                {PLAY_STYLES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`style-opt-${s}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea id="bio" data-testid="profile-bio-input" rows={4} value={form.bio} onChange={(e) => set("bio")(e.target.value)} placeholder="Tell rivals a bit about your game..." />
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <Button data-testid="profile-save-btn" onClick={handleSave} disabled={saving} className="pv-bg-primary text-black font-bold rounded-full px-6 hover:opacity-90">
            {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
            Save changes
          </Button>
        </div>
      </div>

      <SubmitCourtDialog />
    </div>
  );
}
